import { TriState } from "./../models";
import { IPlace } from "./models/Models";
import { CaveRating } from "./../models";
import { formatDistance, formatTemperature } from "./Format";
import { LocationType } from "../models";
import { stringNotNullOrEmpty } from "./Utils";
import { Geo } from "./geo/Geo";
import { ILocation, normalizeLocation } from "../main";
import { Point } from "geojson";

import intersection from "lodash-es/intersection.js";
import uniq from "lodash-es/uniq.js";
import orderBy from "lodash-es/orderBy.js";
import { getWarning } from "./backend/AllWarnings";
``;

//-----------------------------------------------------------------------------
// Constants
//-----------------------------------------------------------------------------
const HYPOXIC_WARNING_DEPTH = 56;
const NARCOSIS_WARNING_DEPTH = 40;

// These are hardcoded from the amplify warning table entries in the amplify db
export const WARNING_OVERHEAD_ENVIRONMENT = "03a37af4-9cb0-489e-b25b-65abef3e84c6";
export const WARNING_TIGHT_RESTRICTIONS = "ac425409-9aae-4833-a681-d664bff2329e";
export const WARNING_CAVE_ENVIRONMENT = "cc7f29e0-e14e-4e6f-b478-6cf40843a8ae";
export const WARNING_AIR_UNSAFE = "cf8ea60e-a361-455a-a2c1-5b0997befd35";
export const WARNING_NARCOSIS = "0f2f9333-b8c3-4c08-9761-7dbfc3b725a5";

export const DIVE_RATINGS = [
    CaveRating.OPEN_WATER,
    CaveRating.BASIC,
    CaveRating.CAVE,
    CaveRating.ADVANCED,
];

/**
 * Get whether a location is a dive or not. This is not definitive as caves, sinkholes etc
 * may also be wet depending on ISpelioData.goesToWater
 * @param type Location type
 * @returns True if the location is wet
 */
export function getLocationTypeIsWet(type: LocationType): boolean {
    return [
        LocationType.WRECK,
        LocationType.REEF,
        LocationType.JETTY,
        LocationType.MUCK,
        LocationType.SAND,
        LocationType.OCEAN_WALL,
    ].includes(type);
}

export function getPlaceDiveRating(place: IPlace): string | undefined {
    if (place.data.spelioData.goesToWater === TriState.YES) {
        if (place.data.spelioData.goesToWater === TriState.YES) {
            const haveLow = ![CaveRating.NONE, CaveRating.UNKNOWN].includes(
                place.data.spelioData.caveRating as CaveRating
            );
            const haveHigh =
                place.data.spelioData.highestCaveRating &&
                ![CaveRating.NONE, CaveRating.UNKNOWN].includes(
                    place.data.spelioData.highestCaveRating as CaveRating
                );
            const lowStr = haveLow
                ? diveRatingToString(place.data.spelioData.caveRating as CaveRating)
                : undefined;
            const highStr = haveHigh
                ? diveRatingToString(place.data.spelioData.highestCaveRating as CaveRating)
                : undefined;

            let text: undefined | string;

            if (haveLow && haveHigh) {
                if (place.data.spelioData.caveRating === place.data.spelioData.highestCaveRating) {
                    text = lowStr;
                } else {
                    text = `${lowStr} - ${highStr}`;
                }
            } else if (haveLow) {
                text = lowStr;
            } else if (haveHigh) {
                text = highStr;
            }

            return text;
        }
    }
    return undefined;
}

export function getPlaceWaterTempText(place: IPlace): string | undefined {
    let text: string | undefined;

    if (place.data.spelioData.goesToWater === TriState.YES && place.data.diveData) {
        const minTemp = place.data.diveData.minTemp;
        const maxTemp = place.data.diveData.maxTemp;

        const haveMinTemp = !!minTemp;
        const haveMaxTemp = !!maxTemp && minTemp !== maxTemp;
        if (haveMinTemp && haveMaxTemp) {
            text = `${formatTemperature(minTemp)} - ${formatTemperature(maxTemp)}`;
        } else if (haveMinTemp) {
            text = `${formatTemperature(minTemp)}`;
        } else if (haveMaxTemp) {
            text = `${formatTemperature(maxTemp)}`;
        }
    }
    return text;
}

export function getPlaceWaterDepthText(place: IPlace): string | undefined {
    let text: string | undefined;

    if (place.data.spelioData.goesToWater === TriState.YES && place.data.diveData) {
        const minDepth = place.data.diveData.minDepth;
        const maxDepth = place.data.diveData.maxDepth;

        const haveMinTemp = !!minDepth;
        const haveMaxTemp = !!maxDepth && minDepth !== maxDepth;
        if (haveMinTemp && haveMaxTemp) {
            text = `${formatDistance(minDepth)} - ${formatDistance(maxDepth)}`;
        } else if (haveMinTemp) {
            text = `${formatDistance(minDepth)}`;
        } else if (haveMaxTemp) {
            text = `${formatDistance(maxDepth)}`;
        }
    }
    return text;
}

/**
 * Get a list of all dive ratings that the place requires.
 *
 * @param place Place to get ratings for
 * @returns Array of cave ratings that the place requires
 */
export function getPlaceDiveRatings(place: IPlace) {
    let lowest: CaveRating | undefined = place.data.spelioData.caveRating as CaveRating;
    let highest: CaveRating | undefined = place.data.spelioData.highestCaveRating as CaveRating;

    if (lowest && [CaveRating.UNKNOWN, CaveRating.NONE].includes(lowest as CaveRating)) {
        lowest = undefined;
    }

    if (highest && [CaveRating.UNKNOWN, CaveRating.NONE].includes(highest as CaveRating)) {
        highest = undefined;
    }

    if (!lowest && !highest) {
        return [];
    } else if (lowest && !highest) {
        return [lowest];
    } else if (!lowest && highest) {
        return [highest];
    }

    const ratings = [
        CaveRating.UNKNOWN,
        CaveRating.NONE,
        CaveRating.OPEN_WATER,
        CaveRating.BASIC,
        CaveRating.CAVE,
        CaveRating.ADVANCED,
    ];

    const l = ratings.indexOf(lowest as CaveRating);
    const h = ratings.indexOf(highest as CaveRating);
    return ratings.slice(l, h + 1);
}

export function placeHasDiveRating(place: IPlace, ratings: CaveRating[] = DIVE_RATINGS) {
    return intersection(getPlaceDiveRatings(place), ratings).length > 0;
}

export function getAutoGeneratedPlaceWarnings(place: IPlace): string[] {
    const warnings: string[] = [];

    const sd = place.data.spelioData;
    if (sd) {
        // cave sites should have cave warning
        if (
            [LocationType.CAVE, LocationType.SINKHOLE, LocationType.MINE].includes(
                place.data.spelioData.type as LocationType
            ) &&
            place.data.spelioData.goesToWater === TriState.YES
        ) {
            warnings.push(WARNING_CAVE_ENVIRONMENT);
        }

        // dive sites which are caves or wrecks should have an overhead warning
        if (
            [
                LocationType.CAVE,
                LocationType.SINKHOLE,
                LocationType.MINE,
                LocationType.WRECK,
            ].includes(place.data.spelioData.type as LocationType) &&
            place.data.spelioData.goesToWater === TriState.YES
        ) {
            warnings.push(WARNING_OVERHEAD_ENVIRONMENT);
        }

        const minDepth = place.data.diveData?.minDepth;
        const maxDepth = place.data.diveData?.maxDepth;

        const depthWarning = (depth: number, warning: string) => {
            if ((minDepth && minDepth >= depth) || (maxDepth && maxDepth >= depth)) {
                warnings.push(warning);
            }
        };
        depthWarning(HYPOXIC_WARNING_DEPTH, WARNING_AIR_UNSAFE);
        depthWarning(NARCOSIS_WARNING_DEPTH, WARNING_NARCOSIS);

        // advanced dive sites should have a restriction warning
        if (placeHasDiveRating(place, [CaveRating.ADVANCED])) {
            warnings.push(WARNING_TIGHT_RESTRICTIONS);
        }
    }

    return warnings;
}

/**
 * Get all warnings specified on a place and auto generated warnings
 */
export function getPlaceWarnings(place: IPlace): string[] {
    const warnings = getAutoGeneratedPlaceWarnings(place);
    return orderBy(
        uniq([...warnings, ...(place.data.warnings ?? [])]).map((x) => getWarning(x)),
        ["level"],
        ["asc"]
    ).map((x) => x.id);
}

export function diveRatingToString(rating: CaveRating): string {
    switch (rating) {
        case CaveRating.BASIC:
            return "Basic cave";
        case CaveRating.ADVANCED:
            return "Advanced cave";
        case CaveRating.OPEN_WATER:
            return "Open water";

        default:
            return rating.toString();
    }
}

export function placeHasWreckData(place: IPlace | undefined) {
    const wd = place?.data.wreckData;
    if (wd) {
        if (
            stringNotNullOrEmpty(wd.built) ||
            stringNotNullOrEmpty(wd.dateSunk) ||
            stringNotNullOrEmpty(wd.type) ||
            wd.length ||
            wd.width ||
            wd.draught
        ) {
            return true;
        }
    }

    return false;
}

export function geoJSONToPlaceDeltas(geo: Geo) {
    const result: {
        name: string;
        location: ILocation;
        spelioData: {
            notes: string;
        };
    }[] = [];

    if (geo.features && geo.features.features) {
        for (const f of geo.features.features) {
            if (f.type === "Feature" && f.geometry.type === "Point" && f.properties) {
                const g = f.geometry as Point;

                result.push({
                    name: f.properties.name,
                    location: normalizeLocation({
                        lat: g.coordinates[1],
                        lon: g.coordinates[0],
                    }),
                    spelioData: {
                        notes: f.properties.description,
                    },
                });
            }
        }
    }

    return result;
}
